import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle } from "lucide-react";

export default function PaymentResult() {
  const location = useLocation();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const isSuccess = location.pathname.includes("success");
  const isSetup = new URLSearchParams(location.search).get("setup") === "1";
  const [status, setStatus] = useState(isSuccess ? "checking" : "cancelled");

  useEffect(() => {
    if (!isSuccess) return;
    const params = new URLSearchParams(location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) { setStatus("error"); return; }
    let attempts = 0;
    let cancelled = false;
    let timer;
    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setStatus("paid"); refreshUser(); return; }
        if (data.status === "expired" || data.payment_status === "failed") { setStatus("error"); return; }
      } catch (err) { console.warn(`Payment status check ${attempts} failed, retrying:`, err); }
      if (cancelled) return;
      if (attempts < 10) timer = setTimeout(poll, 2000);
      else setStatus("timeout");
    };
    poll();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [isSuccess, location.search, refreshUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-sky-50 to-white px-4">
      <div className="w-full max-w-md bg-white rounded-3xl shadow-xl border border-slate-100 p-8 text-center ys-fade-up" data-testid="payment-result">
        <img src={LOGO_URL} alt="Yard-skip" className="w-12 h-12 rounded-xl mx-auto mb-4" />
        {status === "checking" && (<><div className="ys-spinner mx-auto" /><h1 className="font-display text-xl font-bold mt-4">Confirming your payment…</h1><p className="text-slate-500 text-sm mt-2">Hang tight, this only takes a moment.</p></>)}
        {status === "paid" && !isSetup && (<><CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto" /><h1 className="font-display text-2xl font-bold mt-4">You're all set!</h1><p className="text-slate-500 mt-2">Your plan is active. Time to hit the sales.</p><Button onClick={() => navigate("/browse")} data-testid="payment-continue-btn" className="mt-6 w-full rounded-full h-12 bg-ys-sky hover:bg-sky-700">Browse the Map</Button></>)}
        {status === "paid" && isSetup && (<><CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto" /><h1 className="font-display text-2xl font-bold mt-4">Card saved!</h1><p className="text-slate-500 mt-2">Your card is on file. You can now renew or highlight in one tap.</p><Button onClick={() => navigate("/profile?tab=settings")} data-testid="payment-continue-btn" className="mt-6 w-full rounded-full h-12 bg-ys-sky hover:bg-sky-700">Back to Profile</Button></>)}
        {status === "cancelled" && (<><XCircle className="w-16 h-16 text-slate-300 mx-auto" /><h1 className="font-display text-2xl font-bold mt-4">Payment cancelled</h1><p className="text-slate-500 mt-2">No charge was made. You can pick a plan whenever you're ready.</p><Button onClick={() => navigate("/pricing")} className="mt-6 w-full rounded-full h-12 bg-ys-sky hover:bg-sky-700">Back to Plans</Button></>)}
        {(status === "error" || status === "timeout") && (<><XCircle className="w-16 h-16 text-red-400 mx-auto" /><h1 className="font-display text-2xl font-bold mt-4">Something went wrong</h1><p className="text-slate-500 mt-2">We couldn't confirm the payment. If you were charged, it will still activate shortly.</p><Button onClick={() => navigate("/profile")} className="mt-6 w-full rounded-full h-12 bg-ys-sky hover:bg-sky-700">Go to Profile</Button></>)}
      </div>
    </div>
  );
}
